// Wartungsfenster: reine Logik (Normalisieren, Phase, Meldungs-Regeln, Texte). Kein Netzwerk, kein Vue,
// damit sie sich mit dem Node-Testrunner prüfen lässt. Zustand und Polling stehen in maintenance.js.

export const MAINTENANCE_TIME_ZONE = 'Europe/Zurich';

export const KIND_META = {
  planned: { label: 'Geplante Wartung', icon: 'ri-tools-line' },
  emergency: { label: 'Notfall-Wartung', icon: 'ri-alarm-warning-line' },
  info: { label: 'Hinweis', icon: 'ri-information-line' },
};

// Reihenfolge = Schweregrad (höher = gravierender), wird für die Sortierung der Meldungen genutzt.
export const IMPACT_META = {
  none: { label: 'Keine Einschränkung erwartet', short: 'Keine Einschränkung', color: 'info', icon: 'ri-information-line', rank: 0 },
  degraded: { label: 'Eingeschränkter Betrieb', short: 'Eingeschränkt', color: 'warning', icon: 'ri-error-warning-line', rank: 1 },
  partial_outage: { label: 'Teilweise nicht verfügbar', short: 'Teilweise nicht verfügbar', color: 'danger', icon: 'ri-alert-line', rank: 2 },
  full_outage: { label: 'Nicht verfügbar', short: 'Nicht verfügbar', color: 'danger', icon: 'ri-close-circle-line', rank: 3 },
};

export const SERVICE_LABELS = {
  vms: 'Virtuelle Server',
  console: 'Konsole',
  network: 'Netzwerk',
  storage: 'Speicher',
  backups: 'Backups',
  kubernetes: 'Kubernetes',
  portal: 'Kundencenter',
};

export const kindMeta = (kind) => KIND_META[kind] || KIND_META.planned;
export const impactMeta = (impact) => IMPACT_META[impact] || IMPACT_META.degraded;
export const serviceLabel = (service) => SERVICE_LABELS[service] || String(service);

const toMs = (value) => {
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
};

// Antwort des Backends → interne Form mit Millisekunden. Ungültige Einträge (ohne Zeiten oder Id) werden verworfen.
export const normalizeWindow = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const id = Number(raw.id);
  const startsAt = toMs(raw.startsAt);
  const endsAt = toMs(raw.endsAt);
  if (!Number.isInteger(id) || startsAt === null || endsAt === null || endsAt <= startsAt) return null;
  const announceFrom = toMs(raw.announceFrom);
  return {
    id,
    title: String(raw.title || '').trim() || 'Wartungsarbeiten',
    description: String(raw.description || '').trim(),
    kind: KIND_META[raw.kind] ? raw.kind : 'planned',
    impact: IMPACT_META[raw.impact] ? raw.impact : 'degraded',
    startsAt,
    endsAt,
    announceFrom: announceFrom === null ? startsAt - 24 * 3600 * 1000 : announceFrom,
    services: Array.isArray(raw.services) ? raw.services.map(String) : [],
    affectsAllClusters: raw.affectsAllClusters !== false,
    clusters: Array.isArray(raw.clusters)
      ? raw.clusters.map((c) => ({ id: String(c?.id ?? ''), name: String(c?.name || c?.id || '') })).filter((c) => c.id)
      : [],
  };
};

export const normalizeWindows = (list) => (Array.isArray(list) ? list.map(normalizeWindow).filter(Boolean) : []);

// Die Phase wird im Browser aus der Uhrzeit berechnet, damit «geplant» zwischen zwei Abfragen zu «läuft» wird.
export const windowPhase = (w, nowMs) => {
  if (nowMs < w.startsAt) return 'upcoming';
  return nowMs < w.endsAt ? 'active' : 'past';
};

// Laufende zuerst, dann nach Start; nicht mehr aktuelle (vorbei) fallen weg.
export const currentWindows = (list, nowMs) => list
  .filter((w) => windowPhase(w, nowMs) !== 'past')
  .sort((a, b) => {
    const pa = windowPhase(a, nowMs) === 'active' ? 0 : 1;
    const pb = windowPhase(b, nowMs) === 'active' ? 0 : 1;
    return pa - pb || a.startsAt - b.startsAt || a.id - b.id;
  });

// Oben in der App erscheint ein Fenster, wenn es läuft oder die Vorlaufzeit (announceHours, Standard 24 h) begonnen hat.
export const isAnnounced = (w, nowMs) => {
  const phase = windowPhase(w, nowMs);
  return phase === 'active' || (phase === 'upcoming' && nowMs >= w.announceFrom);
};

// Wer die Meldung «geplant» weggeklickt hat, soll sie bei Beginn («läuft jetzt») und nach einer Verschiebung
// wieder sehen. Deshalb gehören Phase und Zeiten zum Schlüssel.
export const dismissalKey = (w, nowMs) => `${w.id}|${w.startsAt}|${w.endsAt}|${windowPhase(w, nowMs)}`;

const DISMISSED_KEEP_MS = 24 * 3600 * 1000;

// Gespeichert wird { schlüssel: endsAt-in-ms }; Einträge, die lange vorbei sind, werden entfernt.
export const pruneDismissed = (map, nowMs) => {
  const result = {};
  Object.entries(map && typeof map === 'object' ? map : {}).forEach(([key, endsAt]) => {
    if (Number.isFinite(endsAt) && endsAt + DISMISSED_KEEP_MS > nowMs) result[key] = endsAt;
  });
  return result;
};

export const bannerWindows = (list, dismissed, nowMs) => currentWindows(list, nowMs)
  .filter((w) => isAnnounced(w, nowMs) && !Object.prototype.hasOwnProperty.call(dismissed || {}, dismissalKey(w, nowMs)));

export const formatDateTime = (ms) => new Date(ms).toLocaleString('de-CH', {
  timeZone: MAINTENANCE_TIME_ZONE, weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

const formatTime = (ms) => new Date(ms).toLocaleTimeString('de-CH', { timeZone: MAINTENANCE_TIME_ZONE, hour: '2-digit', minute: '2-digit' });
const dayKey = (ms) => new Date(ms).toLocaleDateString('sv-SE', { timeZone: MAINTENANCE_TIME_ZONE });

// «Do., 03.11.2026, 22:00 – 00:00 Uhr» bzw. mit zwei Daten, wenn das Fenster über Mitternacht geht.
export const formatRange = (w) => (dayKey(w.startsAt) === dayKey(w.endsAt)
  ? `${formatDateTime(w.startsAt)} – ${formatTime(w.endsAt)} Uhr`
  : `${formatDateTime(w.startsAt)} – ${formatDateTime(w.endsAt)}`);

export const formatDuration = (ms) => {
  const minutes = Math.max(1, Math.round(ms / 60000));
  if (minutes < 60) return `${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 48) return rest ? `${hours} Std. ${rest} Min.` : `${hours} Std.`;
  const days = Math.floor(hours / 24);
  return `${days} Tag${days === 1 ? '' : 'e'}`;
};

// «Beginnt in 3 Std. 20 Min.» / «Läuft noch 45 Min.»
export const relativeText = (w, nowMs) => {
  const phase = windowPhase(w, nowMs);
  if (phase === 'upcoming') return `Beginnt in ${formatDuration(w.startsAt - nowMs)}`;
  if (phase === 'active') return `Läuft noch ${formatDuration(w.endsAt - nowMs)}`;
  return 'Beendet';
};

export const scopeText = (w) => {
  if (w.affectsAllClusters) return 'Ganze Plattform';
  if (w.clusters.length) return `Cluster ${w.clusters.map((c) => c.name).join(', ')}`;
  // Ohne Login liefert das Backend keine Clusternamen.
  return 'Teile der Plattform';
};

export const servicesText = (w) => w.services.map(serviceLabel).join(', ');

// Der gravierendste Eintrag bestimmt die Farbe einer Gruppe.
export const worstImpact = (list) => list.reduce((worst, w) => (impactMeta(w.impact).rank > impactMeta(worst).rank ? w.impact : worst), 'none');

// ── Verwaltung (Admin) ─────────────────────────────────────────────────────

export const KIND_OPTIONS = Object.entries(KIND_META).map(([value, meta]) => ({ value, label: meta.label }));
export const IMPACT_OPTIONS = Object.entries(IMPACT_META).map(([value, meta]) => ({ value, label: meta.label }));
export const SERVICE_OPTIONS = Object.entries(SERVICE_LABELS).map(([value, label]) => ({ value, label }));

export const ADMIN_STATES = [
  { value: 'open', label: 'Aktuell & geplant' },
  { value: 'active', label: 'Laufend' },
  { value: 'upcoming', label: 'Geplant' },
  { value: 'past', label: 'Vergangen' },
  { value: 'cancelled', label: 'Abgesagt' },
  { value: 'all', label: 'Alle' },
];

const zoneFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: MAINTENANCE_TIME_ZONE, hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
});

const zoneParts = (ms) => {
  const parts = {};
  zoneFormatter.formatToParts(new Date(ms)).forEach((p) => { parts[p.type] = Number(p.value); });
  return parts;
};

const zoneOffsetMs = (ms) => {
  const p = zoneParts(ms);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(ms / 1000) * 1000;
};

const pad = (n) => String(n).padStart(2, '0');

// Wert eines <input type="datetime-local"> ↔ Zeitpunkt. Eingaben gelten bewusst als Schweizer Zeit (wie die Anzeige),
// unabhängig von der Zeitzone des Browsers. In der doppelten Stunde der Zeitumstellung gilt die zweite Variante
// nicht; eine nicht existierende Stunde (Frühling) wird um eine Stunde vorgeschoben.
export const zurichLocalToMs = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(String(value || ''));
  if (!match) return null;
  const naive = Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4], +match[5]);
  if (!Number.isFinite(naive)) return null;
  const first = naive - zoneOffsetMs(naive);
  return naive - zoneOffsetMs(first);
};

export const msToZurichLocal = (ms) => {
  const p = zoneParts(ms);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
};

// Zusätzliche Felder, die nur Admins erhalten.
export const normalizeAdminWindow = (raw) => {
  const base = normalizeWindow(raw);
  if (!base) return null;
  return {
    ...base,
    clusterIds: Array.isArray(raw.clusterIds) ? raw.clusterIds.map(String) : [],
    announceHours: Number.isFinite(Number(raw.announceHours)) ? Number(raw.announceHours) : 24,
    internalNote: String(raw.internalNote || ''),
    reference: String(raw.reference || ''),
    cancelledAt: raw.cancelledAt ? Date.parse(raw.cancelledAt) : null,
    createdBy: raw.createdBy || '',
    updatedBy: raw.updatedBy || '',
    updatedAt: raw.updatedAt ? Date.parse(raw.updatedAt) : null,
  };
};

// abgesagt > geplant/laufend/vorbei
export const adminStatus = (w, nowMs) => (w.cancelledAt ? 'cancelled' : windowPhase(w, nowMs));

export const ADMIN_STATUS_META = {
  upcoming: { label: 'Geplant', color: 'primary' },
  active: { label: 'Läuft jetzt', color: 'danger' },
  past: { label: 'Vergangen', color: 'secondary' },
  cancelled: { label: 'Abgesagt', color: 'dark' },
};

// Vorgabe für ein neues Fenster: morgen 22:00–24:00 Schweizer Zeit, 24 Stunden Vorlauf.
export const emptyForm = (nowMs) => {
  const tomorrow = msToZurichLocal(nowMs + 24 * 3600 * 1000).slice(0, 10);
  const startsLocal = `${tomorrow}T22:00`;
  const startsMs = zurichLocalToMs(startsLocal);
  return {
    title: '',
    description: '',
    kind: 'planned',
    impact: 'degraded',
    startsLocal,
    endsLocal: msToZurichLocal(startsMs + 2 * 3600 * 1000),
    affectsAllClusters: true,
    clusterIds: [],
    services: [],
    announceHours: 24,
    internalNote: '',
    reference: '',
  };
};

export const formFromWindow = (w) => ({
  title: w.title,
  description: w.description,
  kind: w.kind,
  impact: w.impact,
  startsLocal: msToZurichLocal(w.startsAt),
  endsLocal: msToZurichLocal(w.endsAt),
  affectsAllClusters: w.affectsAllClusters,
  clusterIds: [...w.clusterIds],
  services: [...w.services],
  announceHours: w.announceHours,
  internalNote: w.internalNote,
  reference: w.reference,
});

// Kopie als Vorlage: gleicher Inhalt, Zeit wie ein neues Fenster (Dauer bleibt erhalten).
export const formAsTemplate = (w, nowMs) => {
  const fresh = emptyForm(nowMs);
  const duration = w.endsAt - w.startsAt;
  return { ...formFromWindow(w), title: `${w.title} (Kopie)`, startsLocal: fresh.startsLocal, endsLocal: msToZurichLocal(zurichLocalToMs(fresh.startsLocal) + duration) };
};

export const MAX_DURATION_MS = 31 * 24 * 3600 * 1000;

// Prüft das Formular und baut den Body für POST/PUT. Fehlertexte entsprechen den Regeln des Backends.
export const formToPayload = (form) => {
  const errors = [];
  const title = String(form.title || '').trim();
  if (!title) errors.push('Titel ist erforderlich.');
  if (title.length > 200) errors.push('Titel darf höchstens 200 Zeichen lang sein.');

  const startsAt = zurichLocalToMs(form.startsLocal);
  const endsAt = zurichLocalToMs(form.endsLocal);
  if (startsAt === null) errors.push('Start ist erforderlich.');
  if (endsAt === null) errors.push('Ende ist erforderlich.');
  if (startsAt !== null && endsAt !== null) {
    if (endsAt <= startsAt) errors.push('Das Ende muss nach dem Start liegen.');
    else if (endsAt - startsAt > MAX_DURATION_MS) errors.push('Ein Wartungsfenster darf höchstens 31 Tage dauern.');
  }

  const announce = Number(form.announceHours);
  if (!Number.isInteger(announce) || announce < 0 || announce > 720) errors.push('Vorlauf muss eine ganze Zahl von 0 bis 720 Stunden sein.');
  if (!form.affectsAllClusters && !form.clusterIds.length) errors.push('Mindestens einen Cluster wählen oder «Ganze Plattform» einschalten.');
  if (String(form.description || '').length > 4000) errors.push('Beschreibung darf höchstens 4000 Zeichen lang sein.');
  if (String(form.internalNote || '').length > 4000) errors.push('Interne Notiz darf höchstens 4000 Zeichen lang sein.');
  if (String(form.reference || '').length > 100) errors.push('Referenz darf höchstens 100 Zeichen lang sein.');
  if (errors.length) return { errors, payload: null };

  return {
    errors,
    payload: {
      title,
      description: String(form.description || '').trim() || null,
      kind: form.kind,
      impact: form.impact,
      startsAt: new Date(startsAt).toISOString(),
      endsAt: new Date(endsAt).toISOString(),
      affectsAllClusters: Boolean(form.affectsAllClusters),
      clusterIds: form.affectsAllClusters ? [] : [...form.clusterIds],
      services: [...form.services],
      announceHours: announce,
      internalNote: String(form.internalNote || '').trim() || null,
      reference: String(form.reference || '').trim() || null,
    },
  };
};
